import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { buildSubscriptionUrl, type SubscriptionRedirectContext } from '@/utils/subscriptionRedirect'
import './SubscriptionModal.css'

interface SubscriptionModalProps {
  isOpen: boolean
  onClose: () => void
  type?: 'no-subscription' | 'limit-reached' | 'upgrade-required' | 'reminder'
  currentPlan?: string | null
  videoCount?: number
  maxVideos?: number | null
  redirectContext?: SubscriptionRedirectContext
  ctaLabel?: string
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onClose,
  type = 'no-subscription',
  currentPlan = null,
  videoCount = 0,
  maxVideos = null,
  redirectContext,
  ctaLabel,
}) => {
  const navigate = useNavigate()

  useEffect(() => {
    if (!isOpen) return

    // Empêcher le scroll du body quand la modal est ouverte
    document.body.style.overflow = 'hidden'
    
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleSubscribe = () => {
    const targetContext: SubscriptionRedirectContext = {
      ...redirectContext,
      from: redirectContext?.from || type,
    }

    navigate(buildSubscriptionUrl(targetContext))
  }

  const handleClose = () => {
    onClose()
  }

  // Modal pour utilisateurs non connectés ou sans abonnement
  if (type === 'no-subscription') {
    return (
      <div className="subscription-modal-overlay">
        <div className="subscription-modal">
          <button className="subscription-modal-close" onClick={handleClose}>
            ×
          </button>
          <div className="subscription-modal-content">
            <div className="subscription-modal-icon">🔒</div>
            <h2 className="subscription-modal-title">
              Accès Premium Requis
            </h2>
            <p className="subscription-modal-description">
              Pour accéder à tout notre contenu adulte, abonnez-vous dès maintenant !
            </p>
            <div className="subscription-modal-benefits">
              <div className="subscription-modal-benefit">
                <span className="benefit-icon">✓</span>
                <span>Accès illimité à tous les contenus</span>
              </div>
              <div className="subscription-modal-benefit">
                <span className="benefit-icon">✓</span>
                <span>Qualité HD et 4K</span>
              </div>
              <div className="subscription-modal-benefit">
                <span className="benefit-icon">✓</span>
                <span>Sans publicité</span>
              </div>
              <div className="subscription-modal-benefit">
                <span className="benefit-icon">✓</span>
                <span>Nouveautés en exclusivité</span>
              </div>
            </div>
            <button 
              className="subscription-modal-button"
              onClick={handleSubscribe}
            >
              {ctaLabel || 'Voir les Abonnements'}
            </button>
            <p className="subscription-modal-footer">
              Vous pouvez continuer à naviguer, mais cette modal réapparaîtra dans 20 secondes
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Modal pour limite atteinte (basic ou premium)
  if (type === 'limit-reached') {
    return (
      <div className="subscription-modal-overlay">
        <div className="subscription-modal">
          <button className="subscription-modal-close" onClick={handleClose}>
            ×
          </button>
          <div className="subscription-modal-content">
            <div className="subscription-modal-icon">⏱️</div>
            <h2 className="subscription-modal-title">
              Limite Quotidienne Atteinte
            </h2>
            <p className="subscription-modal-description">
              Vous avez atteint votre limite de <strong>{maxVideos} vidéos</strong> pour aujourd'hui
              avec votre abonnement <strong>{currentPlan || 'actuel'}</strong>.
            </p>
            <div className="subscription-modal-stats">
              <div className="stat-item">
                <span className="stat-label">Vidéos regardées aujourd'hui</span>
                <span className="stat-value">{videoCount} / {maxVideos}</span>
              </div>
            </div>
            <button 
              className="subscription-modal-button"
              onClick={handleSubscribe}
            >
              {ctaLabel || 'Passer à un Abonnement Supérieur'}
            </button>
            <p className="subscription-modal-footer">
              Revenez demain pour continuer à regarder
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Modal pour upgrade requis (basic vers premium/vip)
  if (type === 'upgrade-required') {
    return (
      <div className="subscription-modal-overlay">
        <div className="subscription-modal">
          <button className="subscription-modal-close" onClick={handleClose}>
            ×
          </button>
          <div className="subscription-modal-content">
            <div className="subscription-modal-icon">⬆️</div>
            <h2 className="subscription-modal-title">
              Montez en Gamme !
            </h2>
            <p className="subscription-modal-description">
              Avec l'abonnement <strong>{currentPlan || 'actuel'}</strong>, vous êtes limité à <strong>{maxVideos} vidéos par jour</strong>.
              Passez à un abonnement supérieur pour profiter de plus de contenus !
            </p>
            <div className="subscription-modal-comparison">
              <div className="comparison-item current">
                <strong>{currentPlan || 'actuel'}</strong>
                <span>{maxVideos} vidéos/jour</span>
              </div>
              <div className="comparison-item upgrade">
                <strong>PREMIUM/VIP</strong>
                <span>Jusqu'à illimité</span>
              </div>
            </div>
            <button 
              className="subscription-modal-button"
              onClick={handleSubscribe}
            >
              {ctaLabel || 'Améliorer mon Abonnement'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (type === 'reminder') {
    return (
      <div className="subscription-modal-overlay">
        <div className="subscription-modal">
          <button className="subscription-modal-close" onClick={handleClose}>
            ×
          </button>
          <div className="subscription-modal-content reminder">
            <div className="subscription-modal-icon">🔥</div>
            <h2 className="subscription-modal-title">
              Vous hésitez encore ?
            </h2>
            <p className="subscription-modal-description">
              Profitez de <strong>7 jours d'essai Premium pour 3,99€</strong> et regardez immédiatement
              {redirectContext?.videoTitle ? (
                <>
                  {' '}
                  « {redirectContext.videoTitle} »
                </>
              ) : (
                ' la vidéo qui vous tentait.'
              )}
            </p>
            <div className="subscription-modal-benefits">
              <div className="subscription-modal-benefit">
                <span className="benefit-icon">⚡</span>
                <span>Accès instantané à tout le catalogue</span>
              </div>
              <div className="subscription-modal-benefit">
                <span className="benefit-icon">🔒</span>
                <span>Paiement discret et sécurisé</span>
              </div>
              <div className="subscription-modal-benefit">
                <span className="benefit-icon">❌</span>
                <span>Annulez en un clic, sans engagement</span>
              </div>
            </div>
            <button 
              className="subscription-modal-button"
              onClick={handleSubscribe}
            >
              {ctaLabel || "Activer l'essai 7 jours"}
            </button>
            <p className="subscription-modal-footer">
              Offre valable une fois par compte. Vous pouvez annuler à tout moment avant la fin de l'essai.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return null
}

export default SubscriptionModal
